/**
 * Whiteboard Thumbnail Generator
 * static/js/whiteboard/thumbnail.js
 * 
 * 縮圖生成功能
 */

import { THUMBNAIL_CONFIG } from './config.js';
import { getExcalidrawAPI } from './state.js';

/**
 * 生成縮圖 base64
 * @param {Object} options - 可選配置
 * @returns {Promise<string|null>} - base64 字串或 null
 */
export async function generateThumbnail(options = {}) {
  const api = getExcalidrawAPI();
  if (!api) {
    console.warn('Excalidraw API not available for thumbnail generation');
    return null;
  }

  try {
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    const files = api.getFiles();

    // 如果沒有元素，返回 null
    if (!elements || elements.length === 0) {
      return null;
    }

    const config = {
      maxWidth: options.maxWidth || THUMBNAIL_CONFIG.maxWidth,
      maxHeight: options.maxHeight || THUMBNAIL_CONFIG.maxHeight,
      quality: options.quality || THUMBNAIL_CONFIG.quality,
    };

    // 使用 Excalidraw 的 exportToBlob API
    if (typeof window.ExcalidrawLib !== 'undefined' && window.ExcalidrawLib.exportToBlob) {
      const blob = await window.ExcalidrawLib.exportToBlob({
        elements,
        appState: {
          ...appState,
          exportBackground: true,
          exportWithDarkMode: false,
          viewBackgroundColor: '#ffffff',
        },
        files,
        maxWidthOrHeight: Math.max(config.maxWidth, config.maxHeight),
        quality: config.quality,
      });

      return await blobToBase64(blob);
    }

    // 回退方案：使用 canvas 截圖
    return await generateThumbnailFromCanvas(config);
  } catch (error) {
    console.error('Error generating thumbnail:', error);
    return null;
  }
}

/**
 * 從 canvas 生成縮圖（回退方案）
 * @param {Object} config 
 * @returns {Promise<string|null>}
 */
async function generateThumbnailFromCanvas(config) {
  const previewArea = document.querySelector('.preview-area svg');
  if (!previewArea) {
    return null;
  }

  try {
    // 複製 SVG
    const svgClone = previewArea.cloneNode(true);
    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    // 建立 canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    return new Promise((resolve) => {
      img.onload = () => {
        // 計算縮放比例
        const scale = Math.min(
          config.maxWidth / img.width,
          config.maxHeight / img.height,
          1
        );
        
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        // 繪製白色背景
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 繪製圖片
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // 轉換為 base64
        const dataUrl = canvas.toDataURL('image/png', config.quality);
        URL.revokeObjectURL(svgUrl);
        resolve(dataUrl);
      };

      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        resolve(null);
      };

      img.src = svgUrl;
    });
  } catch (error) {
    console.error('Error generating thumbnail from canvas:', error);
    return null;
  }
}

/**
 * Blob 轉 base64
 * @param {Blob} blob 
 * @returns {Promise<string>}
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * 驗證縮圖大小
 * @param {string} base64 
 * @param {number} maxKB 
 * @returns {boolean}
 */
export function validateThumbnailSize(base64, maxKB = 500) {
  if (!base64) return true;
  
  // base64 長度約為原始大小的 4/3
  const estimatedBytes = (base64.length * 3) / 4;
  const maxBytes = maxKB * 1024;
  
  return estimatedBytes <= maxBytes;
}

/**
 * 壓縮縮圖（如果太大）
 * @param {string} base64 
 * @param {number} maxKB 
 * @returns {Promise<string>}
 */
export async function compressThumbnail(base64, maxKB = 500) {
  if (!base64 || validateThumbnailSize(base64, maxKB)) {
    return base64;
  }

  try {
    // 降低品質重新生成
    const img = new Image();
    
    return new Promise((resolve) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // 縮小尺寸
        const scale = 0.7;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        resolve(canvas.toDataURL('image/png', 0.5));
      };
      
      img.onerror = () => resolve(base64);
      img.src = base64;
    });
  } catch {
    return base64;
  }
}
